/**
 * Share codes: a level packed into a compact binary form and written as base64url, so a whole
 * puzzle fits in a URL hash. Positions are stored in whole world units and angles in half
 * degrees; a CRC-8 catches typos and truncated links.
 *
 * Layout (version 1): version, name, sources, receivers, walls, rocks, dust, nebulae, fixed
 * masses, inventory (5 counts), par, solution, crc. Numbers are unsigned LEB128 varints.
 */
import { MASS_KINDS, WORLD_H, WORLD_W } from './constants';
import { validateLevel } from './level';
import type { Level, MassKind } from './types';

const VERSION = 1;

class Writer {
  bytes: number[] = [];
  u8(v: number) {
    this.bytes.push(v & 0xff);
  }
  uint(v: number) {
    let n = Math.max(0, Math.round(v));
    do {
      let b = n & 0x7f;
      n = Math.floor(n / 128);
      if (n > 0) b |= 0x80;
      this.bytes.push(b);
    } while (n > 0);
  }
  x(v: number) {
    this.uint(Math.min(WORLD_W, Math.max(0, v)));
  }
  y(v: number) {
    this.uint(Math.min(WORLD_H, Math.max(0, v)));
  }
}

class Reader {
  i = 0;
  constructor(private bytes: Uint8Array) {}
  get done() {
    return this.i >= this.bytes.length;
  }
  u8(): number {
    if (this.i >= this.bytes.length) throw new Error('code is truncated');
    return this.bytes[this.i++]!;
  }
  uint(): number {
    let result = 0;
    let scale = 1;
    for (let k = 0; k < 5; k++) {
      const b = this.u8();
      result += (b & 0x7f) * scale;
      if ((b & 0x80) === 0) return result;
      scale *= 128;
    }
    throw new Error('number is too long');
  }
  count(max: number): number {
    const n = this.uint();
    if (n > max) throw new Error('too many items');
    return n;
  }
}

/** CRC-8 (polynomial 0x07). */
export function crc8(bytes: ArrayLike<number>): number {
  let crc = 0;
  for (let i = 0; i < bytes.length; i++) {
    crc ^= bytes[i]!;
    for (let k = 0; k < 8; k++) crc = crc & 0x80 ? ((crc << 1) ^ 0x07) & 0xff : (crc << 1) & 0xff;
  }
  return crc;
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

export function toBase64Url(bytes: ArrayLike<number>): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i]!;
    const b = i + 1 < bytes.length ? bytes[i + 1]! : 0;
    const c = i + 2 < bytes.length ? bytes[i + 2]! : 0;
    const n = (a << 16) | (b << 8) | c;
    out += B64[(n >> 18) & 63]! + B64[(n >> 12) & 63]!;
    if (i + 1 < bytes.length) out += B64[(n >> 6) & 63]!;
    if (i + 2 < bytes.length) out += B64[n & 63]!;
  }
  return out;
}

export function fromBase64Url(text: string): Uint8Array {
  const clean = text.trim();
  if (!/^[A-Za-z0-9_-]*$/.test(clean)) throw new Error('code contains invalid characters');
  if (clean.length % 4 === 1) throw new Error('code has the wrong length');
  const out: number[] = [];
  for (let i = 0; i < clean.length; i += 4) {
    const chunk = clean.slice(i, i + 4);
    let n = 0;
    for (let k = 0; k < 4; k++) n = (n << 6) | (k < chunk.length ? B64.indexOf(chunk[k]!) : 0);
    out.push((n >> 16) & 0xff);
    if (chunk.length > 2) out.push((n >> 8) & 0xff);
    if (chunk.length > 3) out.push(n & 0xff);
  }
  return new Uint8Array(out);
}

const halfDeg = (a: number) => Math.round((((a % 360) + 360) % 360) * 2) % 720;

export function encodeLevel(level: Level): string {
  const w = new Writer();
  w.u8(VERSION);
  const name = new TextEncoder().encode(level.name.slice(0, 60));
  w.uint(name.length);
  name.forEach((b) => w.u8(b));

  w.uint(level.sources.length);
  for (const s of level.sources) {
    const beams = Math.max(1, Math.min(3, s.beams ?? 1));
    w.x(s.x);
    w.y(s.y);
    w.uint(halfDeg(s.angle));
    w.u8(s.color | ((beams - 1) << 3));
    if (beams > 1) w.uint(Math.round((s.spread ?? 0) * 2));
  }
  w.uint(level.receivers.length);
  for (const r of level.receivers) {
    const count = Math.max(1, Math.min(3, r.count ?? 1));
    w.x(r.x);
    w.y(r.y);
    w.u8(r.color | ((count - 1) << 3));
  }
  w.uint(level.walls.length);
  for (const k of level.walls) {
    w.x(k.x1);
    w.y(k.y1);
    w.x(k.x2);
    w.y(k.y2);
  }
  for (const list of [level.rocks, level.dust]) {
    w.uint(list.length);
    for (const d of list) {
      w.x(d.x);
      w.y(d.y);
      w.uint(d.r);
    }
  }
  w.uint(level.nebulae.length);
  for (const n of level.nebulae) {
    w.x(n.x);
    w.y(n.y);
    w.uint(n.r);
    w.u8(n.color);
  }
  const masses = (list: { kind: MassKind; x: number; y: number }[]) => {
    w.uint(list.length);
    for (const m of list) {
      w.u8(MASS_KINDS.indexOf(m.kind));
      w.x(m.x);
      w.y(m.y);
    }
  };
  masses(level.fixed);
  for (const k of MASS_KINDS) w.u8(level.inventory[k] ?? 0);
  w.uint(level.par);
  masses(level.solution);
  w.u8(crc8(w.bytes));
  return toBase64Url(w.bytes);
}

export type Decoded = { ok: true; level: Level } | { ok: false; error: string };

export function decodeLevel(code: string): Decoded {
  try {
    const bytes = fromBase64Url(code);
    if (bytes.length < 3) throw new Error('code is too short');
    const body = bytes.subarray(0, bytes.length - 1);
    if (crc8(body) !== bytes[bytes.length - 1])
      throw new Error('code is damaged (checksum mismatch)');
    const r = new Reader(body);
    const version = r.u8();
    if (version !== VERSION) throw new Error(`unknown code version ${version}`);
    const nameLen = r.count(240);
    const nameBytes: number[] = [];
    for (let i = 0; i < nameLen; i++) nameBytes.push(r.u8());
    const name = new TextDecoder().decode(new Uint8Array(nameBytes)) || 'Shared plate';

    const sources = Array.from({ length: r.count(8) }, () => {
      const x = r.uint();
      const y = r.uint();
      const angle = r.uint() / 2;
      const packed = r.u8();
      const beams = ((packed >> 3) & 3) + 1;
      const src: Record<string, number> = { x, y, angle, color: packed & 7 };
      if (beams > 1) {
        src.beams = beams;
        src.spread = r.uint() / 2;
      }
      return src;
    });
    const receivers = Array.from({ length: r.count(8) }, () => {
      const x = r.uint();
      const y = r.uint();
      const packed = r.u8();
      const count = ((packed >> 3) & 3) + 1;
      return count > 1 ? { x, y, color: packed & 7, count } : { x, y, color: packed & 7 };
    });
    const walls = Array.from({ length: r.count(40) }, () => ({
      x1: r.uint(),
      y1: r.uint(),
      x2: r.uint(),
      y2: r.uint(),
    }));
    const disc = () => ({ x: r.uint(), y: r.uint(), r: r.uint() });
    const rocks = Array.from({ length: r.count(40) }, disc);
    const dust = Array.from({ length: r.count(40) }, disc);
    const nebulae = Array.from({ length: r.count(40) }, () => ({ ...disc(), color: r.u8() }));
    const masses = () =>
      Array.from({ length: r.count(12) }, () => {
        const kind = MASS_KINDS[r.u8()];
        if (!kind) throw new Error('unknown mass kind');
        return { kind, x: r.uint(), y: r.uint() };
      });
    const fixed = masses();
    const inventory: Record<string, number> = {};
    for (const k of MASS_KINDS) {
      const v = r.u8();
      if (v > 0) inventory[k] = v;
    }
    const par = r.uint();
    const solution = masses();
    if (!r.done) throw new Error('code has trailing data');

    const v = validateLevel({
      id: 'custom',
      name,
      sources,
      receivers,
      walls,
      rocks,
      dust,
      nebulae,
      fixed,
      inventory,
      par,
      solution,
    });
    if (!v.ok) return { ok: false, error: v.errors[0] ?? 'invalid level' };
    return v;
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'unreadable code' };
  }
}

/** Round a level to what a share code can store (whole units, half degrees). */
export function quantizeLevel(level: Level): Level {
  const q = (v: number) => Math.round(v);
  const a = (v: number) => halfDeg(v) / 2;
  return {
    ...level,
    sources: level.sources.map((s) => ({
      ...s,
      x: q(s.x),
      y: q(s.y),
      angle: a(s.angle),
      ...(s.spread !== undefined ? { spread: Math.round(s.spread * 2) / 2 } : {}),
    })),
    receivers: level.receivers.map((r) => ({ ...r, x: q(r.x), y: q(r.y) })),
    walls: level.walls.map((w) => ({ x1: q(w.x1), y1: q(w.y1), x2: q(w.x2), y2: q(w.y2) })),
    rocks: level.rocks.map((d) => ({ x: q(d.x), y: q(d.y), r: q(d.r) })),
    dust: level.dust.map((d) => ({ x: q(d.x), y: q(d.y), r: q(d.r) })),
    nebulae: level.nebulae.map((n) => ({ ...n, x: q(n.x), y: q(n.y), r: q(n.r) })),
    fixed: level.fixed.map((m) => ({ ...m, x: q(m.x), y: q(m.y) })),
    solution: level.solution.map((m) => ({ ...m, x: q(m.x), y: q(m.y) })),
    par: Math.round(level.par),
  };
}
